/**
 * Helpers for the embedded web app → native mobile shell bridge.
 *
 * The native shell (mobile-native repo) injects `window.ReactNativeWebView` and
 * handles posted action messages in its `WebViewIntegrator`. Action messages
 * have the shape `{ type: 'action', action: string, payload?: any }` — the same
 * shape used by NativeIntegrator on the mobile side.
 */

/** True when the web app is running inside the native mobile WebView. */
export function isRunningInNativeApp(): boolean {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView
}

/**
 * True when running in the native shell and it advertises `capability` in
 * `window.__STX_NATIVE_CAPABILITIES__`. Older builds drop actions they don't
 * know, so gate every new action on its capability.
 */
export function nativeSupports(capability: string): boolean {
  return (
    isRunningInNativeApp() &&
    !!window.__STX_NATIVE_CAPABILITIES__?.includes(capability)
  )
}

/**
 * Post an action message to the native shell. No-op outside the native app.
 * Carries the shell's bridge token when it injected one, so the shell can tell
 * this page's messages from those of embedded frames.
 *
 * @returns true if the message was handed to the native shell.
 */
export function postNativeAction(action: string, payload?: unknown): boolean {
  if (!isRunningInNativeApp()) return false

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: 'action',
      action,
      payload,
      token: window.__STX_BRIDGE_TOKEN__,
    }),
  )
  return true
}

/** True when the native shell advertises that it handles the OPEN-PDF action. */
export function nativeSupportsOpenPdf(): boolean {
  return nativeSupports('OPEN-PDF')
}

/**
 * True when the User Portal is embedded in the native shell, which then owns
 * entering projects and signing out (OPEN-PROJECT / SIGN-OUT /
 * SESSION-EXPIRED actions).
 */
export function isNativePortalHost(): boolean {
  return nativeSupports('PORTAL')
}

/**
 * Ask the native shell to open a document URL in the OS's native, streamed PDF
 * viewer (SFSafariViewController on iOS / Chrome Custom Tabs on Android),
 * handled by the `OPEN-PDF` action in the mobile WebViewIntegrator.
 *
 * Rendering large PDFs (77–90 MB) inline with pdf.js inside the single WKWebView
 * loads the whole file into the WebContent process and OOM-jetsams it
 * ("Webview process terminated", STX1-603). The native viewer streams pages on
 * demand and is memory-bounded.
 *
 * No-op when not running in the native app — callers should fall back to a
 * normal browser open (e.g. a new tab) for mobile web.
 *
 * @returns true if the request was handed to the native shell.
 */
export function openPdfInNativeApp(url: string, fileName?: string): boolean {
  // Only hand off when the shell understands OPEN-PDF; older builds would drop
  // the message and leave the user with no preview.
  if (!nativeSupportsOpenPdf()) return false

  return postNativeAction('OPEN-PDF', { url, fileName })
}

/** True when the native shell advertises that it handles the SHARE-FILE action. */
export function nativeSupportsShareFile(): boolean {
  return nativeSupports('SHARE-FILE')
}

/**
 * Hand a file to the native shell's share sheet, handled by the `SHARE-FILE` action in the mobile
 * WebViewIntegrator. Android's WebView has no Web Share API, so the file crosses the bridge as
 * base64; a phone's Share map file is 1–6 MB, which takes well under a second.
 *
 * @returns true if the file was handed to the native shell.
 */
export async function shareFileInNativeApp(file: File): Promise<boolean> {
  if (!nativeSupportsShareFile()) return false

  const base64 = await readAsBase64(file)
  return postNativeAction('SHARE-FILE', {
    fileName: file.name,
    mimeType: file.type,
    base64,
  })
}

function readAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
